import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore'; // Asegúrate de importar doc y updateDoc
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { db } from '../utils/firebaseConfig';
import GeneralNavbar from './GeneralNavbar';
import './AdminProductList.css';

function AdminProductList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const [descriptionSearchTerm, setDescriptionSearchTerm] = useState('');
  const [limit, setLimit] = useState(10); // Ahora sí estamos usando limit
  const [idSearchTerm, setIdSearchTerm] = useState('');

  const navigate = useNavigate();

  const handleBackClick = () => {
    navigate('/admin');
  };

  const filterProducts = useCallback(async () => {
    setLoading(true);
    try {
      const productsRef = collection(db, 'products');
      let filteredProductsList = [];
  
      // Filtro por ID
      if (idSearchTerm) {
        const idQuery = query(productsRef, where('id', '==', Number(idSearchTerm)));
        const idQuerySnapshot = await getDocs(idQuery);
        filteredProductsList = idQuerySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } else {
        // Obtener todos los productos y filtrar en el cliente
        const querySnapshot = await getDocs(productsRef);
        filteredProductsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
  
        // Aplicar filtro por nombre y descripción en el cliente
        if (nameSearchTerm) {
          filteredProductsList = filteredProductsList.filter((product) =>
            product.name.toLowerCase().includes(nameSearchTerm.toLowerCase())
          );
        }
  
        if (descriptionSearchTerm) {
          filteredProductsList = filteredProductsList.filter((product) =>
            product.description.toLowerCase().includes(descriptionSearchTerm.toLowerCase())
          );
        }
      }
  
      // Ordenar por ID
      const sortedProducts = filteredProductsList.sort((a, b) => a.id - b.id);
      setProducts(sortedProducts);
    } catch (error) {
      console.error('Error filtering products: ', error);
    } finally {
      setLoading(false);
    }
  }, [idSearchTerm, nameSearchTerm, descriptionSearchTerm]);
  

  useEffect(() => {
    filterProducts();
  }, [filterProducts]);

  const toggleProductStatus = async (numericId, currentStatus) => {
    try {
      const productsRef = collection(db, 'products');
      const querySnapshot = await getDocs(query(productsRef, where('id', '==', numericId)));
  
      if (querySnapshot.empty) {
        console.error('No se encontró el producto con el id numérico:', numericId);
        return;
      }

      const productDoc = querySnapshot.docs[0];
      const productRef = doc(db, 'products', productDoc.id);
  
      await updateDoc(productRef, { enabled: !currentStatus });

      filterProducts();
    } catch (error) {
      console.error('Error updating product status: ', error);
    }
  };

  const handleNameSearch = (e) => {
    setNameSearchTerm(e.target.value);
  };

  const handleDescriptionSearch = (e) => {
    setDescriptionSearchTerm(e.target.value);
  };

  const handleIdSearch = (e) => {
    setIdSearchTerm(e.target.value);
  };

  const loadMoreProducts = () => {
    setLimit((prevLimit) => prevLimit + 3);
  };

  const generatePDF = () => {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.text('Lista de Productos (ordenados por categoría)', 14, 10);

  // Ordenar por categoría, luego por nombre
  const sortedProducts = [...products].sort((a, b) => {
    const categoryCompare = a.category.localeCompare(b.category);
    if (categoryCompare !== 0) return categoryCompare;
    const nameCompare = a.name.localeCompare(b.name);
    if (nameCompare !== 0) return nameCompare;
    return a.id - b.id;
  });

  // Crear tabla única
  autoTable(doc, {
    startY: 20,
    head: [['Código', 'Marca', 'Descripción', 'Categoría', 'Precio']],
    body: sortedProducts.map((product) => [
      product.id,
      product.name,
      product.description,
       product.category,
      `$${parseFloat(product.price).toFixed(2)}`
    ]),
    theme: 'striped',
    styles: { fontSize: 10 },
    headStyles: { fillColor: [0, 128, 255] },
    columnStyles: {
      0: { cellWidth: 20 }, // Código
      1: { cellWidth: 35 }, // Marca
      2: { cellWidth: 70 }, // Descripción
      3: { cellWidth: 35 }, // Categoria
      4: { cellWidth: 25, halign: 'right' } // Precio
    }
  });

  doc.save('productos_ordenados_por_categoria.pdf');
};



  return (
    <div className="admin-product-list">
      <GeneralNavbar title="Lista de Productos" onBackClick={handleBackClick} />
      <div className="search-bar">
        <input
          type="number"
          placeholder="Buscar por ID..."
          value={idSearchTerm}
          onChange={handleIdSearch}
          className="search-input"
        />
        <input
          type="text"
          placeholder="Buscar por Marca..."
          value={nameSearchTerm}
          onChange={handleNameSearch}
          className="search-input"
        />
        <input
          type="text"
          placeholder="Buscar por descripción..."
          value={descriptionSearchTerm}
          onChange={handleDescriptionSearch}
          className="search-input"
        />
        <button onClick={generatePDF} className="generate-pdf-button">Generar PDF</button>
      </div>
      {loading ? (
        <p className="centered-text">Cargando...</p>
      ) : products.length === 0 ? (
        <p className="centered-text">No hay productos.</p>
      ) : (
        <>
          <ul>
            {products.slice(0, limit).map((product) => (
              <li key={product.id} className={product.enabled ? '' : 'disabled'}>
                <Link to={`/admin/products/edit/${product.id}`}>
                  {product.id} - {product.name} - {product.description} - {product.category} - stock: {product.stock} - ${product.price}
                </Link>
                <button onClick={() => toggleProductStatus(product.id, product.enabled)}>
                  {product.enabled ? 'Deshabilitar' : 'Habilitar'}
                </button>
              </li>
            ))}
          </ul>
          {limit < products.length && (
            <button onClick={loadMoreProducts} className="load-more-button">
              Cargar más
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default AdminProductList;
